import { useState } from "react";
import { Post } from "../../../types/posts";
import { YouTubeVideoComment } from "./YouTubeVideoComment";
import { getRelativeTime } from "./YouTubePost";
import { useIsMobile } from "../../../hooks/useIsMobile";

type Tab = "description" | "comment";

export default function YouTubeShortFooter({post}: {post: Post}) {
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("description");
  const DESCRIPTION_LIMIT = 420;
  const isMobile = useIsMobile(900);

  const inPostFooter =
    post?.footer && post.footerType === "in-post" ? post.footer : "";

  const fullContent = [
    post?.content || "",
    inPostFooter,
    // Hashtags are now part of content directly — appending from youtubeHashtags field is disabled.
    // Array.isArray(post?.youtubeHashtags) && post.youtubeHashtags.length > 0
    //   ? post.youtubeHashtags.join(" ")
    //   : "",
  ]
    .filter(Boolean)
    .join("\n\n");

  const isLong = fullContent.length > DESCRIPTION_LIMIT;

  const displayText =
    !expanded && isLong
      ? fullContent.slice(0, DESCRIPTION_LIMIT) + "..."
      : fullContent;

  const timeAgo = getRelativeTime(post);

  return (
    <div className="w-full aspect-[9/16] mx-auto bg-white rounded-xl py-4 flex flex-col">
      {/* ROW 1 - Tabs */}
      <div className="flex border-b border-gray-200 mb-3">
        {(["description", "comment"] as Tab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 pb-3 text-sm font-semibold capitalize border-b-2 -mb-px transition-colors ${
              activeTab === tab
                ? "border-black text-black"
                : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === "description" ? (
        <div className="flex-1 px-4 overflow-y-auto min-h-0">
          {/* ROW — Title */}
          <p className="text-sm font-bold text-gray-900 mb-3">
            {post?.title}
          </p>

          {/* ROW — Avatar + channel name */}
          <div className="flex items-center gap-3 mb-4 border-b border-gray-200 pb-4">
            {post?.userImage ? (
              <img
                src={post.userImage}
                alt="Channel"
                className="w-9 h-9 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <span className="w-9 h-9 flex items-center justify-center bg-gray-300 text-xs font-semibold rounded-full flex-shrink-0">
                {post?.displayName
                  ? post.displayName.split(" ").filter(Boolean).map((n: string) => n[0]).join("").toUpperCase().slice(0, 2)
                  : "YT"}
              </span>
            )}
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-semibold text-gray-900 truncate">
                {post?.displayName || "Channel Name"}
              </span>
            </div>
          </div>

          {/* ROW 3 */}
          <div className="flex justify-between text-center mb-4">
            <div className="flex-1">
              <p className="font-semibold">1.2K</p>
              <p className="text-xs text-gray-500">Likes</p>
            </div>

            <div className="flex-1">
              <p className="font-semibold">8.5K</p>
              <p className="text-xs text-gray-500">Views</p>
            </div>

            <div className="flex-1">
              <p className="font-semibold">{timeAgo || "0m"}</p>
              <p className="text-xs text-gray-500">Ago</p>
            </div>
          </div>

          {/* ROW 4 - DESCRIPTION BOX */}
          <div className="text-sm text-gray-800 whitespace-pre-line break-words overflow-hidden bg-[#f2f2f2] hover:bg-[#d9d9d9] rounded-xl p-3" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
            {((post as any)?.contentType === "Caption Video" || (post as any)?.type === "Caption Video") &&
            !post?.content &&
            (post?.status?.toLowerCase() === "draft" || post?.status?.toLowerCase() === "videoprocessing") ? (
              <div className="space-y-2 py-1">
                <div className="h-3 bg-gray-200 rounded w-3/4 shimmer-bar" />
                <div className="h-3 bg-gray-200 rounded w-1/2 shimmer-bar" />
              </div>
            ) : (
            <p>
              {displayText.split(/(#\w+)/g).map((part, i) =>
                part.startsWith("#") ? (
                  <span key={i} className="text-blue-600 cursor-pointer">{part}</span>
                ) : (
                  part
                )
              )}
            </p>
            )}

            {isLong && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="mt-3 text-sm font-medium text-gray-700"
              >
                {expanded ? "Show less" : "Read more"}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className={isMobile ? "px-0 pb-1" : "px-4 pb-1"}>
          {post?.footerType === "comment" && post?.footer ? (
            <YouTubeVideoComment post={post} />
          ) : (
            <p className="text-sm text-gray-400 text-center mt-6">No comments</p>
          )}
        </div>
      )}
    </div>
  );
}